import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import UploadDropzone from "@/components/UploadDropzone";
import { USE_CASES, USE_CASE_MAP } from "@/data/useCases";
import { useAuth } from "@/context/AuthContext";
import ResultPack from "@/pages/ResultPack";
import { FileText, ArrowRight, RotateCw } from "lucide-react";

// Stages: idle -> uploading -> classifying -> confirm -> reviewing -> done
export default function WorkspacePage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [stage, setStage] = useState("idle");
  const [error, setError] = useState("");
  const [doc, setDoc] = useState(null); // {doc_id, filename, classification, page_count, kind}
  const [selectedType, setSelectedType] = useState("");
  const [role, setRole] = useState("customer");
  const [jobDescription, setJobDescription] = useState("");
  const [review, setReview] = useState(null);

  const startedFromHomeRef = useRef(false);

  const doUpload = useCallback(async (file) => {
    setError("");
    setStage("uploading");
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/documents/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDoc(data);
      setSelectedType(data.classification?.document_type || "other");
      setStage("confirm");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setStage("idle");
    }
  }, []);

  // Pick up pending file from home / usecase-detail
  useEffect(() => {
    if (startedFromHomeRef.current) return;
    const f = window.__PDF_REVIEW_PENDING_FILE__;
    const preType = window.__PDF_REVIEW_PENDING_TYPE__;
    if (f) {
      startedFromHomeRef.current = true;
      window.__PDF_REVIEW_PENDING_FILE__ = null;
      window.__PDF_REVIEW_PENDING_TYPE__ = null;
      doUpload(f).then(() => {
        if (preType) setSelectedType(preType);
      });
    }
  }, [doUpload]);

  const runReview = async () => {
    setError("");
    setStage("reviewing");
    try {
      const context = {};
      if (selectedType === "resume") context.job_description = jobDescription;
      if (selectedType === "msa") context.role = role;
      const { data } = await api.post("/reviews/run", {
        doc_id: doc.doc_id,
        document_type: selectedType,
        context,
      });
      setReview(data);
      setStage("done");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setStage("confirm");
    }
  };

  const reset = () => {
    setDoc(null);
    setReview(null);
    setSelectedType("");
    setJobDescription("");
    setError("");
    setStage("idle");
  };

  const cls = doc?.classification;
  const confidence = Math.round((cls?.confidence || 0) * 100);
  const detected = USE_CASE_MAP[cls?.document_type];

  const canRun = useMemo(() => {
    if (!selectedType) return false;
    if (selectedType === "resume" && jobDescription.trim().length < 20) return false;
    return true;
  }, [selectedType, jobDescription]);

  // RENDER

  if (stage === "done" && review) {
    return (
      <ResultPack
        review={review}
        onReset={reset}
        onSaveAccount={() => navigate("/signup", { state: { from: "/history" } })}
        savedToAccount={!!user && typeof user === "object"}
      />
    );
  }

  return (
    <section
      data-testid="workspace-page"
      className="max-w-[1280px] mx-auto px-6 md:px-10 py-12"
    >
      {stage === "idle" && (
        <div className="max-w-3xl">
          <div className="eyebrow mb-4">Workspace</div>
          <h1 className="font-serif-display text-3xl md:text-4xl text-ink leading-tight">
            Upload the document you'd like reviewed.
          </h1>
          <p className="mt-4 text-ink-70">Max 20 MB · 50 pages · PDF or DOCX.</p>
          <div className="mt-8">
            <UploadDropzone onFile={doUpload} testId="workspace-dropzone" />
          </div>
          {error && (
            <div
              data-testid="workspace-error"
              className="mt-6 text-sm text-[var(--severity-red-text)] bg-[var(--severity-red-bg)] border border-[var(--severity-red-border)] px-3 py-2"
            >
              {error}
            </div>
          )}
        </div>
      )}

      {stage === "uploading" && (
        <ProgressPanel
          title="Uploading & extracting text…"
          hint="Reading pages and preserving citations."
        />
      )}

      {stage === "confirm" && doc && (
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="paper-card">
              <div className="eyebrow mb-4">Document</div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 border border-ink-20 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-ink" strokeWidth={1.5} />
                </div>
                <div className="min-w-0">
                  <div
                    data-testid="workspace-filename"
                    className="font-medium text-ink truncate"
                  >
                    {doc.filename}
                  </div>
                  <div className="text-xs text-ink-50 font-mono-plex uppercase tracking-widest mt-1">
                    {doc.kind} · {doc.page_count} page{doc.page_count === 1 ? "" : "s"}
                  </div>
                </div>
              </div>

              <div className="hairline my-6" />

              <div className="eyebrow mb-3">Detected type</div>
              <div className="flex items-baseline justify-between">
                <div
                  className="font-serif-display text-2xl text-ink"
                  data-testid="workspace-detected-type"
                >
                  {detected?.label || "Generic document"}
                </div>
                <div className="font-mono-plex text-sm text-ink-70">{confidence}%</div>
              </div>
              <div className="mt-3 h-1.5 bg-ink-05 relative overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 bg-forest"
                  style={{ width: `${confidence}%`, transition: "width 400ms" }}
                />
              </div>
              {cls?.reasoning && (
                <p className="mt-3 text-xs text-ink-50 leading-relaxed">
                  {cls.reasoning}
                </p>
              )}
            </div>
          </div>

          <div className="md:col-span-7">
            <div className="eyebrow mb-3">Confirm or switch</div>
            <h2 className="font-serif-display text-2xl md:text-3xl text-ink leading-tight">
              Which review would you like to run?
            </h2>
            <p className="mt-3 text-ink-70 text-sm">
              We picked the highest-confidence match. Pick a different playbook if that
              doesn't feel right.
            </p>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {USE_CASES.map((u) => (
                <button
                  key={u.key}
                  data-testid={`workspace-type-${u.key}`}
                  onClick={() => setSelectedType(u.key)}
                  className={`text-left border px-4 py-3 transition-colors ${
                    selectedType === u.key
                      ? "border-ink bg-white"
                      : "border-ink-10 bg-surface hover:border-ink-20"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono-plex text-xs text-ink-50 tracking-widest">
                      {u.number}
                    </span>
                    {selectedType === u.key && (
                      <span className="text-xs font-mono-plex text-forest uppercase tracking-widest">
                        Selected
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-sm text-ink font-medium">{u.label}</div>
                </button>
              ))}
              <button
                data-testid="workspace-type-other"
                onClick={() => setSelectedType("other")}
                className={`text-left border px-4 py-3 transition-colors ${
                  selectedType === "other"
                    ? "border-ink bg-white"
                    : "border-ink-10 bg-surface hover:border-ink-20"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono-plex text-xs text-ink-50 tracking-widest">
                    07
                  </span>
                  {selectedType === "other" && (
                    <span className="text-xs font-mono-plex text-forest uppercase tracking-widest">
                      Selected
                    </span>
                  )}
                </div>
                <div className="mt-1 text-sm text-ink font-medium">
                  Generic document (fallback)
                </div>
              </button>
            </div>

            {selectedType === "msa" && (
              <div className="mt-6 paper-card">
                <div className="eyebrow mb-3">Your role</div>
                <div className="flex gap-2">
                  {[
                    { v: "customer", l: "We are the customer" },
                    { v: "vendor", l: "We are the vendor" },
                    { v: "unspecified", l: "Neither / unsure" },
                  ].map((o) => (
                    <button
                      key={o.v}
                      data-testid={`workspace-role-${o.v}`}
                      onClick={() => setRole(o.v)}
                      className={`px-3 py-1.5 text-xs font-mono-plex uppercase tracking-widest border ${
                        role === o.v
                          ? "bg-ink text-white border-ink"
                          : "text-ink-70 border-ink-20 hover:border-ink"
                      }`}
                    >
                      {o.l}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {selectedType === "resume" && (
              <div className="mt-6 paper-card">
                <div className="eyebrow mb-3">Paste the job description</div>
                <textarea
                  data-testid="workspace-jd-textarea"
                  className="w-full border border-ink-20 px-3 py-2.5 bg-white outline-none focus-ring text-sm font-mono-plex"
                  rows={8}
                  value={jobDescription}
                  onChange={(e) => setJobDescription(e.target.value)}
                  placeholder="Role, responsibilities, required and nice-to-have skills, years of experience…"
                />
                <p className="mt-2 text-xs text-ink-50">
                  We compare the resume text against this JD.
                </p>
              </div>
            )}

            {error && (
              <div
                data-testid="workspace-error"
                className="mt-6 text-sm text-[var(--severity-red-text)] bg-[var(--severity-red-bg)] border border-[var(--severity-red-border)] px-3 py-2"
              >
                {error}
              </div>
            )}

            <div className="mt-8 flex items-center gap-3">
              <button
                data-testid="workspace-run-btn"
                className="btn-primary focus-ring"
                disabled={!canRun}
                onClick={runReview}
              >
                Run review <ArrowRight className="w-4 h-4" />
              </button>
              <button
                data-testid="workspace-restart-btn"
                className="btn-ghost focus-ring"
                onClick={reset}
              >
                <RotateCw className="w-4 h-4" /> Start over
              </button>
            </div>
          </div>
        </div>
      )}

      {stage === "reviewing" && (
        <ProgressPanel
          title={`Running ${
            USE_CASE_MAP[selectedType]?.label || "review"
          } playbook…`}
          hint="Reading every clause, drafting the memo, and citing pages."
        />
      )}
    </section>
  );
}

function ProgressPanel({ title, hint }) {
  return (
    <div data-testid="workspace-progress" className="max-w-2xl mx-auto py-20 text-center">
      <div className="eyebrow mb-6">Processing</div>
      <div className="font-serif-display text-3xl md:text-4xl text-ink leading-tight">
        {title}
      </div>
      <p className="mt-4 text-ink-70">{hint}</p>
      <div className="mt-10 progress-shimmer max-w-md mx-auto" />
    </div>
  );
}
